import PropTypes from "prop-types"
import React, { Component } from "react"
import { hot } from "react-hot-loader/root"
import { connect } from "react-redux"
import Select from "react-select"
import { bindActionCreators } from "redux"
import { ActionCreators } from "../../../redux/actions"
import ApiServices from "../../../services/ApiService"

class ConnectWellToMelTemplate extends Component {
  state = {
    creating: false,
    createError: null,
    melIdOverride: undefined,
    melIdOverrideActive: false,
    selectedTemplateIdOverride: null,
    persistError: null,
  }

  MEL_BASE_URL = "https://keystone-mel.prod-azure.akerbp.com"

  componentDidUpdate(prevProps) {
    const prevId = prevProps?.data?.well?.mel_id
    const nextId = this.props?.data?.well?.mel_id
    if (prevId !== nextId && this.state.melIdOverrideActive) {
      this.setState({ melIdOverride: undefined, melIdOverrideActive: false })
    }
    const prevTemplateId = prevProps?.data?.well?.mel_template_id
    const nextTemplateId = this.props?.data?.well?.mel_template_id
    if (prevTemplateId !== nextTemplateId && this.state.selectedTemplateIdOverride) {
      this.setState({ selectedTemplateIdOverride: null })
    }
  }

  componentDidMount() {
    const { getMelTemplateList, getMelList } = this.props
    getMelTemplateList()
    getMelList()
  }

  onTemplateChange = (elem) => {
    const { onFieldUpdate, data } = this.props
    const id = elem ? elem.id : null
    onFieldUpdate("well.mel_template_id", id, data.id)
    this.setState({ selectedTemplateIdOverride: id })
    this.persistMelLink({ mel_id: data?.well?.mel_id || null, mel_template_id: id })
  }

  onExistingMelChange = (elem) => {
    const { onFieldUpdate, data } = this.props
    const melId = elem ? elem.id || elem.mel_id || elem.melId : null
    onFieldUpdate("well.mel_id", melId, data.id)
    if (melId) {
      onFieldUpdate("well.mel_template_id", null, data.id)
    }
    this.setState({ melIdOverride: melId, melIdOverrideActive: true })
    this.persistMelLink({ mel_id: melId, mel_template_id: null })
  }

  onUnlink = () => {
    const { onFieldUpdate, data } = this.props
    onFieldUpdate("well.mel_id", null, data.id)
    onFieldUpdate("well.mel_template_id", null, data.id)
    this.setState({ melIdOverride: null, melIdOverrideActive: true })
    this.persistMelLink({ mel_id: null, mel_template_id: null })
  }

  persistMelLink = async ({ mel_id, mel_template_id }) => {
    const { data } = this.props
    if (!data?.well?.id) return
    try {
      await ApiServices.postDataToApi(`well/${data.well.id}/mel`, {
        mel_id,
        mel_template_id,
      })
      this.setState({ persistError: null })
    } catch (error) {
      this.setState({
        persistError: "Failed to save MEL linkage. Please try again.",
      })
    }
  }

  onCreateMel = async () => {
    const { data, scheduleId, onFieldUpdate, getMelList } = this.props
    const templateId =
      this.state.selectedTemplateIdOverride || data?.well?.mel_template_id

    if (!scheduleId || !data?.id || !templateId) {
      this.setState({ createError: "Select a MEL template first." })
      return
    }

    this.setState({ creating: true, createError: null })
    try {
      const result = await ApiServices.postDataToApi(
        `schedule/mel/createFromWellInstance/${scheduleId}/${data.id}`,
        { allowUpdate: true, template: templateId }
      )
      const melData = result && result.data ? result.data : result
      const melId =
        melData && (melData.id || melData.mel_id || melData?.data?.id)
      if (melId) {
        onFieldUpdate("well.mel_id", melId, data.id)
      }
      getMelList()
    } catch (error) {
      this.setState({
        createError: "Failed to create MEL. Please try again.",
      })
    } finally {
      this.setState({ creating: false })
    }
  }

  getMelOptionLabel = (option) => {
    if (!option) return ""
    const displayId = option.id || option.mel_id || option.melId
    const name =
      option.name ||
      option.external_title ||
      option.external_id ||
      `MEL ${displayId}`
    const start = option.start || option.start_date || option.startDate
    const end = option.end || option.end_date || option.endDate
    if (start || end) {
      return `${name} (${start || "?"} - ${end || "?"})`
    }
    return name
  }

  render() {
    const { melTemplates, melList, failedStatusChange, data, scheduleId } =
      this.props
    const { creating, createError, persistError } = this.state
    const normalizedMelList = (melList || []).map((item) => ({
      ...item,
      id: item.id || item.mel_id || item.melId,
    }))
    const selectedTemplateId = data?.well?.mel_template_id
    const selectedTemplate = selectedTemplateId
      ? melTemplates.find((e) => String(e.id) === String(selectedTemplateId))
      : null
    const selectedMelId = this.state.melIdOverrideActive
      ? this.state.melIdOverride
      : data?.well?.mel_id
    const selectedMel = selectedMelId
      ? normalizedMelList.find((e) => String(e.id) === String(selectedMelId))
      : null
    const melBaseUrl =
      this.props?.domain?.data?.melBaseUrl ||
      this.MEL_BASE_URL
    const canCreate = !!(scheduleId && selectedTemplate && !creating)
    const hasLinkedMel = !!selectedMelId

    return (
      <div
        id="mel"
        className={`Details-edit__section${
          failedStatusChange === "plan_for_execution"
            ? " validation-failed"
            : ""
        }`}
      >
        <h3>MEL</h3>
        <div style={{ marginBottom: "10px" }}>
          <span style={{ fontSize: "12px", display: "block" }}>
            MEL environment:{" "}
            <a
              href={melBaseUrl}
              className="FontStyle--Bold"
              style={{ color: "#ffffff", textDecoration: "underline" }}
              target="_blank"
              rel="noreferrer"
            >
              {melBaseUrl}
            </a>
          </span>
        </div>
        <div style={{ marginBottom: "10px" }}>
          <strong>Create new MEL from template</strong>
          <Select
            options={melTemplates}
            closeMenuOnSelect={true}
            isClearable={true}
            placeholder={"Select MEL template"}
            isMulti={false}
            className={`Select__Dropdown nonCentered`}
            getOptionLabel={(option) => option["name"]}
            classNamePrefix="Select__Dropdown__Elm"
            onChange={this.onTemplateChange}
            name="mel_template_id"
            isDisabled={data?.well?.mel_id !== null}
            value={selectedTemplate || null}
          />
          <div style={{ marginTop: "8px", display: "flex", gap: "8px" }}>
            <button
              type="button"
              className="Panel__Button--submit"
              disabled={!canCreate}
              onClick={this.onCreateMel}
            >
              {creating ? "Creating..." : "Create MEL now"}
            </button>
          </div>
          {createError && (
            <span style={{ fontSize: "12px", color: "#b00020" }}>
              {createError}
            </span>
          )}
          {persistError && (
            <span style={{ fontSize: "12px", color: "#b00020" }}>
              {persistError}
            </span>
          )}
        </div>
        <div style={{ marginBottom: "10px" }}>
          <strong>Currently linked MEL</strong>
          <Select
            options={normalizedMelList}
            closeMenuOnSelect={true}
            isClearable={true}
            placeholder={"Select existing MEL"}
            isMulti={false}
            className={`Select__Dropdown nonCentered`}
            getOptionLabel={this.getMelOptionLabel}
            getOptionValue={(option) => option.id}
            classNamePrefix="Select__Dropdown__Elm"
            onChange={this.onExistingMelChange}
            name="mel_id"
            value={selectedMel || null}
          />
        </div>
        {hasLinkedMel && (
          <>
            <div
              style={{ fontSize: "12px", marginTop: "10px", display: "block" }}
            >
              Active MEL:{" "}
              <span className="FontStyle--Bold">
                {selectedMel?.name ||
                  selectedMel?.external_title ||
                  selectedMel?.external_id ||
                  selectedMelId}
              </span>
              <br />
              Active MEL ID:{" "}
              <span className="FontStyle--Bold">{selectedMelId}</span>
              <br />
              MELs can be managed by visiting the MEL environment:{" "}
              <a
                href={`${melBaseUrl}/mels/${selectedMelId}`}
                className="FontStyle--Bold"
                style={{ color: "#ffffff", textDecoration: "underline" }}
                target="_blank"
                rel="noreferrer"
              >
                View in MEL →
              </a>
            </div>
            <div
              style={{ fontSize: "12px", marginTop: "6px", display: "block" }}
            >
              Changing an existing association here does not delete anything in
              MEL.
            </div>
            <div style={{ marginTop: "8px", display: "flex", gap: "8px" }}>
              <button
                type="button"
                className="Panel__Button--cancel"
                onClick={this.onUnlink}
              >
                Unlink MEL
              </button>
            </div>
          </>
        )}
      </div>
    )
  }
}

ConnectWellToMelTemplate.propTypes = {
  data: PropTypes.object,
  domain: PropTypes.object,
  onFieldUpdate: PropTypes.func,
  failedStatusChange: PropTypes.string,
  melTemplates: PropTypes.array,
  melList: PropTypes.array,
  scheduleId: PropTypes.number,
  getMelTemplateList: PropTypes.func,
  getMelList: PropTypes.func,
  units: PropTypes.string,
  currency: PropTypes.string,
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(ActionCreators, dispatch)
}

function mapStateToProps(state) {
  return {
    melTemplates: state.application.melTemplates,
    melList: state.application.melList,
    domain: state.application.domain,
    scheduleId: state.schedulerData.schedulerData.data
      ? state.schedulerData.schedulerData.data.id
      : null,
  }
}

export default hot(
  connect(mapStateToProps, mapDispatchToProps)(ConnectWellToMelTemplate)
)
